import { JSX, type ReactNode, useEffect, useState } from 'react';

import { env } from '@/shared/lib/config/env';
import {
  AUTH_BLOCK_CODE_DEACTIVATED,
  clearAuthBlockState,
  getAuthBlockState,
  getLocalStorageItem,
  setLocalStorageItem,
  subscribeToAuthBlockState,
} from '@/shared/utils';

import { getAuthUrl } from '../../../api/base';

const is3dOnlyQuery = () => env.VITE_AUTH_PROVIDER === '3d-only-query';

const AuthStatusScreen = ({ message, color = '#4a5568' }: { message: string; color?: string }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 24,
      fontFamily: 'system-ui, sans-serif',
      color,
      textAlign: 'center',
    }}
  >
    <p style={{ margin: 0, fontSize: 18 }}>{message}</p>
  </div>
);

const AuthBlockedScreen = ({ message }: { message: string }) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleRetry = async () => {
    setIsRedirecting(true);
    clearAuthBlockState();

    try {
      const url = await getAuthUrl();
      window.location.href = url;
    } catch {
      setIsRedirecting(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
        fontFamily: 'system-ui, sans-serif',
        color: '#c53030',
        textAlign: 'center',
        gap: 16,
      }}
    >
      <p style={{ margin: 0, fontSize: 18 }}>{message}</p>
      <button
        type="button"
        onClick={() => void handleRetry()}
        disabled={isRedirecting}
        style={{
          border: 0,
          borderRadius: 8,
          padding: '10px 16px',
          cursor: isRedirecting ? 'default' : 'pointer',
          background: '#1a202c',
          color: '#fff',
        }}
      >
        {isRedirecting ? 'Перенаправляем…' : 'Попробовать снова'}
      </button>
    </div>
  );
};

type AuthBootstrapState = {
  isReady: boolean;
  hasToken: boolean;
  authError: string | null;
  authErrorCode: string | null;
};

const INITIAL_AUTH_BOOTSTRAP_STATE: AuthBootstrapState = {
  isReady: false,
  hasToken: false,
  authError: null,
  authErrorCode: null,
};
let authBootstrapState: AuthBootstrapState = INITIAL_AUTH_BOOTSTRAP_STATE;
let authBootstrapStarted = false;
const authBootstrapListeners = new Set<() => void>();

const notifyAuthBootstrapListeners = () => {
  authBootstrapListeners.forEach((listener) => listener());
};

const setAuthBootstrapState = (nextState: AuthBootstrapState) => {
  authBootstrapState = nextState;
  notifyAuthBootstrapListeners();
};

const subscribeToAuthBootstrap = (listener: () => void) => {
  authBootstrapListeners.add(listener);
  return () => {
    authBootstrapListeners.delete(listener);
  };
};

const buildBootstrapStateFromStorage = (): AuthBootstrapState => {
  const token = getLocalStorageItem('token');
  if (token) {
    return { isReady: true, hasToken: true, authError: null, authErrorCode: null };
  }

  const authBlockState = getAuthBlockState();
  if (authBlockState) {
    return {
      isReady: true,
      hasToken: false,
      authError: authBlockState.message,
      authErrorCode: authBlockState.code,
    };
  }

  return authBootstrapState;
};

const bootstrapAuth = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const tokenParam = env.VITE_AUTH_TOKEN_QUERY_PARAM;
  // Если в URL несколько token (редукт: ?token=старый&token=новый), берём последний — он актуальный
  const tokensFromUrl = urlParams.getAll(tokenParam);
  const tokenFromUrl = tokensFromUrl.length > 0 ? tokensFromUrl[tokensFromUrl.length - 1] : null;

  if (tokenFromUrl) {
    clearAuthBlockState();
    setLocalStorageItem('token', tokenFromUrl);
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
    setAuthBootstrapState({ isReady: true, hasToken: true, authError: null, authErrorCode: null });
    return;
  }

  const token = getLocalStorageItem('token');
  if (token) {
    clearAuthBlockState();
    setAuthBootstrapState({ isReady: true, hasToken: true, authError: null, authErrorCode: null });
    return;
  }

  const authBlockState = getAuthBlockState();
  if (authBlockState) {
    setAuthBootstrapState({
      isReady: true,
      hasToken: false,
      authError: authBlockState.message,
      authErrorCode: authBlockState.code,
    });
    return;
  }

  if (is3dOnlyQuery()) {
    setAuthBootstrapState({
      isReady: true,
      hasToken: false,
      authError: 'Отсутствует токен авторизации',
      authErrorCode: null,
    });
    return;
  }

  try {
    const url = await getAuthUrl();
    window.location.href = url;
  } catch {
    setAuthBootstrapState({
      isReady: true,
      hasToken: false,
      authError: 'Не удалось выполнить перенаправление на страницу входа.',
      authErrorCode: null,
    });
  }
};

const ensureAuthBootstrap = () => {
  if (authBootstrapStarted) {
    return;
  }

  authBootstrapStarted = true;
  void bootstrapAuth();
};

export const AuthBootstrapProvider = ({ children }: { children: ReactNode }) => children;

export const useAuthBootstrap = (): AuthBootstrapState => {
  const [state, setState] = useState<AuthBootstrapState>(authBootstrapState);

  useEffect(() => {
    const syncState = () => {
      const nextState = buildBootstrapStateFromStorage();
      authBootstrapState = nextState;
      setState(nextState);
    };

    const unsubscribe = subscribeToAuthBootstrap(syncState);
    const unsubscribeAuthBlock = subscribeToAuthBlockState(syncState);
    ensureAuthBootstrap();

    // Важный anti-race sync: bootstrap может синхронно завершиться
    // (например, при возврате с ?token=...) ещё до того, как effect
    // завершит установку подписки и произойдёт первый re-render.
    syncState();

    return () => {
      unsubscribe();
      unsubscribeAuthBlock();
    };
  }, []);

  return state;
};

export const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const { authError, authErrorCode, hasToken, isReady } = useAuthBootstrap();

  if (authError) {
    if (authErrorCode === AUTH_BLOCK_CODE_DEACTIVATED) {
      return <AuthBlockedScreen message={authError} />;
    }
    return <AuthStatusScreen message={authError} color="#c53030" />;
  }

  if (!isReady) {
    return <AuthStatusScreen message="Проверяем авторизацию..." />;
  }

  if (!hasToken) {
    return <AuthStatusScreen message="Перенаправляем на страницу входа..." />;
  }

  return children;
};
